import React from 'react';
import { Button, Loading } from '@/components';
import { States } from '@/helpers/common/states';
import Communication from '@/model/Communication';
import CommunicationStatus from '@/model/CommunicationStatus';
import CommunicationType from '@/model/CommunicationType';

interface Props {
  state: States;
  communication: Communication | null;
  onBack: () => void;
  onEdit: () => void;
}

const getChannelLabel = (channel: CommunicationType): string => {
  switch (channel) {
    case CommunicationType.WHATSAPP:
      return 'WhatsApp';
    case CommunicationType.EMAIL:
      return 'E-mail';
    case CommunicationType.PUSH_NOTIFICATION:
      return 'Notificação';
    default:
      return '-';
  }
};

const getStatusLabel = (status?: CommunicationStatus): string => {
  switch (status) {
    case CommunicationStatus.AWAITING:
      return 'Aguardando';
    case CommunicationStatus.PROCESSING:
      return 'Processando';
    case CommunicationStatus.PROCESSED:
      return 'Processada';
    case CommunicationStatus.ERROR:
      return 'Erro';
    default:
      return '-';
  }
};

export const CommunicationViewUI: React.FC<Props> = ({
  state,
  communication,
  onBack,
  onEdit,
}): JSX.Element => {
  const channels = communication?.channels?.length
    ? communication.channels.map(getChannelLabel).join(', ')
    : '-';

  const locations = communication?.locations?.length
    ? communication.locations.map(location => `${location.city} - ${location.state}`).join(', ')
    : '-';

  return (
    <>
      <Loading isVisible={state === States.loading} />

      <div className="d-flex justify-content-center">
        <div
          style={{
            width: '100%',
            maxWidth: 1240,
            padding: '12px 10px',
          }}
        >
          <div
            className="bg-white shadow-sm"
            style={{
              borderRadius: 15,
              padding: 24,
            }}
          >
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-3 mb-4">
              <div>
                <h2
                  style={{
                    fontSize: 34,
                    fontWeight: 800,
                    marginBottom: 6,
                    color: '#4d4f50',
                  }}
                >
                  Detalhes da campanha
                </h2>
                <div style={{ color: '#6b7280', fontWeight: 500 }}>
                  Visualize as informações completas da campanha.
                </div>
              </div>

              <div className="d-flex gap-2">
                <Button
                  title="Voltar"
                  theme="outlineDark"
                  onClick={onBack}
                  style={{ marginRight: 5 }}
                />
                <Button title="Editar" theme="dark" onClick={onEdit} />
              </div>
            </div>

            {!communication ? (
              <div
                style={{
                  padding: 24,
                  border: '1px solid #e5e7eb',
                  borderRadius: 12,
                  color: '#6b7280',
                  fontWeight: 600,
                }}
              >
                Nenhuma campanha encontrada.
              </div>
            ) : (
              <div className="d-flex flex-column gap-4">
                <div
                  style={{
                    border: '1px solid #e5e7eb',
                    borderRadius: 12,
                    padding: 20,
                    marginBottom: 5,
                  }}
                >
                  <div
                    style={{ fontSize: 22, fontWeight: 800, color: '#4d4f50', marginBottom: 18 }}
                  >
                    Informações gerais
                  </div>

                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
                      gap: 16,
                    }}
                  >
                    <div>
                      <div style={{ fontWeight: 800, color: '#4d4f50' }}>Nome da campanha</div>
                      <div style={{ color: '#111827', marginTop: 4 }}>
                        {communication.name || '-'}
                      </div>
                    </div>

                    <div>
                      <div style={{ fontWeight: 800, color: '#4d4f50' }}>Status</div>
                      <div style={{ color: '#111827', marginTop: 4 }}>
                        {getStatusLabel(communication.communicationStatus)}
                      </div>
                    </div>

                    <div>
                      <div style={{ fontWeight: 800, color: '#4d4f50' }}>Evento</div>
                      <div style={{ color: '#111827', marginTop: 4 }}>
                        {communication.event || '-'}
                      </div>
                    </div>

                    <div>
                      <div style={{ fontWeight: 800, color: '#4d4f50' }}>Qtd. de mensagens</div>
                      <div style={{ color: '#111827', marginTop: 4 }}>
                        {communication.customerLimit ?? 0}
                      </div>
                    </div>

                    <div>
                      <div style={{ fontWeight: 800, color: '#4d4f50' }}>Canais</div>
                      <div style={{ color: '#111827', marginTop: 4 }}>{channels}</div>
                    </div>

                    <div>
                      <div style={{ fontWeight: 800, color: '#4d4f50' }}>Raio</div>
                      <div style={{ color: '#111827', marginTop: 4 }}>
                        {communication.radius ?? 0} km
                      </div>
                    </div>
                  </div>
                </div>

                <div
                  style={{
                    border: '1px solid #e5e7eb',
                    borderRadius: 12,
                    padding: 20,
                    marginBottom: 5,
                  }}
                >
                  <div
                    style={{ fontSize: 22, fontWeight: 800, color: '#4d4f50', marginBottom: 18 }}
                  >
                    Localização
                  </div>

                  <div>
                    <div style={{ fontWeight: 800, color: '#4d4f50' }}>Locais atendidos</div>
                    <div style={{ color: '#111827', marginTop: 4 }}>{locations}</div>
                  </div>
                </div>

                <div
                  style={{
                    border: '1px solid #e5e7eb',
                    borderRadius: 12,
                    padding: 20,
                    marginBottom: 5,
                  }}
                >
                  <div
                    style={{ fontSize: 22, fontWeight: 800, color: '#4d4f50', marginBottom: 18 }}
                  >
                    Tipo
                  </div>

                  <div className="d-flex flex-column gap-3">
                    <div>
                      <div style={{ fontWeight: 800, color: '#4d4f50' }}>Template</div>
                      <div style={{ color: '#111827', marginTop: 4 }}>
                        {communication.template || '-'}
                      </div>
                    </div>

                    {communication.mensage && (
                      <div>
                        <div style={{ fontWeight: 800, color: '#4d4f50' }}>Mensagem</div>
                        <div
                          style={{
                            color: '#111827',
                            marginTop: 4,
                            whiteSpace: 'pre-wrap',
                            border: '1px solid #e5e7eb',
                            borderRadius: 10,
                            padding: 12,
                            background: '#f9fafb',
                          }}
                        >
                          {communication.mensage || '-'}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div
                  style={{
                    border: '1px solid #e5e7eb',
                    borderRadius: 12,
                    padding: 20,
                    marginBottom: 5,
                  }}
                >
                  <div
                    style={{ fontSize: 22, fontWeight: 800, color: '#4d4f50', marginBottom: 18 }}
                  >
                    Agendamento
                  </div>

                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
                      gap: 16,
                    }}
                  >
                    <div>
                      <div style={{ fontWeight: 800, color: '#4d4f50' }}>Data inicial</div>
                      <div style={{ color: '#111827', marginTop: 4 }}>
                        {communication.startDate
                          ? new Date(communication.startDate).toLocaleString('pt-BR')
                          : '-'}
                      </div>
                    </div>

                    <div>
                      <div style={{ fontWeight: 800, color: '#4d4f50' }}>Data final</div>
                      <div style={{ color: '#111827', marginTop: 4 }}>
                        {communication.endDate
                          ? new Date(communication.endDate).toLocaleString('pt-BR')
                          : '-'}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};
